/**
 * API types for the /companies endpoint
 * These types match the backend CompanyDto model in CreditControl.Models
 */

export interface CompanyApi {
  id: number;
  name: string;
  code: string | null;
  companyGroupId: number;
  // Provisioning metadata (set during automatic provisioning)
  evoSubscriptionId: string | null;
  evoOrganisationId: string | null;
  // Integration linking (set when user manually links to external product)
  linkedSubscriptionId: string | null;
  linkedSubscriptionName: string | null;
  linkedProductId: string | null;
  linkedProductName: string | null;
  linkedDatabaseName: string | null;
  // External company mapping
  externalCompanyCode: string | null;
  externalCompanyName: string | null;
  isMultiCompany: boolean;
  // Other properties
  currencyCode: string | null;
  taxId: string | null;
  status: 'active' | 'pending_setup';
  createdAt: string;
  lastSyncedAt: string | null;
  assignedUserCount: number;
}

export interface CurrencyApi {
  id: number;
  code: string;
  name: string;
  symbol: string | null;
}

export interface CreateCompanyRequest {
  name: string;
  code?: string | null;
  taxId?: string | null;
  linkedSubscriptionId?: string | null;
  linkedSubscriptionName?: string | null;
  linkedProductId?: string | null;
  linkedProductName?: string | null;
  linkedDatabaseName?: string | null;
  externalCompanyCode?: string | null;
  externalCompanyName?: string | null;
  isMultiCompany?: boolean;
  addressLine?: string | null;
  country?: string | null;
  vatNumber?: string | null;
  companyRegistrationNumber?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface UpdateCompanyRequest {
  name: string;
  code?: string | null;
  taxId?: string | null;
  externalCompanyCode?: string | null;
  externalCompanyName?: string | null;
  status: string;
  addressLine?: string | null;
  country?: string | null;
  vatNumber?: string | null;
  companyRegistrationNumber?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface UpdateCompanyLinkRequest {
  linkedSubscriptionId?: string | null;
  linkedSubscriptionName?: string | null;
  linkedProductId?: string | null;
  linkedProductName?: string | null;
  linkedDatabaseName?: string | null;
  externalCompanyCode?: string | null;
  externalCompanyName?: string | null;
  isMultiCompany?: boolean;
}

// Product IDs for linking
export const FINANCIALS_PRODUCT_ID = '1f6ad618-1714-4e09-aa5a-8a0b5f1b4bb4';
export const DIMENSIONS_VNEXT_PRODUCT_ID = '63cc79e9-e977-489a-aebe-3103d2076e16';

export interface CompanyDetailsApi {
  companyId: number;
  addressLine: string | null;
  country: string | null;
  vatNumber: string | null;
  companyRegistrationNumber: string | null;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
  updatedAt: string | null;
}

export interface UpsertCompanyDetailsRequest {
  addressLine?: string | null;
  country?: string | null;
  vatNumber?: string | null;
  companyRegistrationNumber?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface UploadCompanyLogoResponse {
  logoUrl: string;
}

export interface CompanyBankDetailsApi {
  id: number;
  companyId: number;
  currency: string | null;
  sortCode: string | null;
  accountNumber: string | null;
  accountName: string | null;
  ibanNumber: string | null;
  swiftBicCode: string | null;
  updatedAt: string;
}

export interface CreateCompanyBankDetailsRequest {
  currency?: string | null;
  sortCode?: string | null;
  accountNumber?: string | null;
  accountName?: string | null;
  ibanNumber?: string | null;
  swiftBicCode?: string | null;
}

export interface UpdateCompanyBankDetailsRequest {
  currency?: string | null;
  sortCode?: string | null;
  accountNumber?: string | null;
  accountName?: string | null;
  ibanNumber?: string | null;
  swiftBicCode?: string | null;
}

export interface CompanyProfileApi {
  details: CompanyDetailsApi;
  bankDetails: CompanyBankDetailsApi[];
}

export interface CompanyWithProfileApi extends CompanyApi {
  details: CompanyDetailsApi;
  bankDetails: CompanyBankDetailsApi[];
}

export interface UpsertCompanyProfileRequest {
  details: UpsertCompanyDetailsRequest;
  bankDetails: BankDetailsItemRequest[];
}

export interface BankDetailsItemRequest {
  currency?: string | null;
  sortCode?: string | null;
  accountNumber?: string | null;
  accountName?: string | null;
  ibanNumber?: string | null;
  swiftBicCode?: string | null;
}
